import { parseISO } from 'date-fns';
import { format, utcToZonedTime } from 'date-fns-tz';
import { useTranslation } from 'next-i18next';
import Link from 'next/link';
import { useMemo, VFC } from 'react';

import { useCreation } from '@/apis/aniftyBackend/creations/getCreation';
import CustomButton from '@/components/CustomButton';
import FloatingModal from '@/components/FloatingModal';
import FramedIcon from '@/components/FramedIcon';
import SquareCreationThumbnail from '@/components/SquareCreationThumbnail';
import routes from '@/config/routes';

import styles from './ReleaseReminderCompletedModal.module.scss';

type Props = {
  creationID: number;
  isOpen: boolean;
  onRequestClose(): void;
};

const ReleaseReminderCompletedModal: VFC<Props> = ({
  creationID,
  isOpen,
  onRequestClose,
}: Props) => {
  const { t, i18n } = useTranslation();
  const { data: creation } = useCreation({ creationID });

  // Guess current user's time zone
  const currentUsersTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;

  let displayTimeZone = 'UTC';
  if (currentUsersTimeZone === 'Asia/Tokyo') {
    // Display in JST only if user is in Japan
    displayTimeZone = 'JST';
  }

  const reservedAt = creation?.reservedAt
    ? parseISO(creation.reservedAt)
    : null;

  const reservationDate = useMemo(() => {
    if (!reservedAt) {
      return '';
    }

    // Guess current user's time zone
    return format(reservedAt, 'LLL d, yyyy', {
      timeZone: displayTimeZone,
    });
  }, [reservedAt, displayTimeZone]);

  const reservationTime = useMemo(() => {
    if (!reservedAt) {
      return '';
    }

    const zonedTime = utcToZonedTime(creation.reservedAt, displayTimeZone);

    if (format(zonedTime, 'mm') === '00') {
      // Omit minutes if date is on hour
      return `at ${format(zonedTime, 'K a')} ${displayTimeZone}`;
    }
    return `at ${format(zonedTime, 'K:mm a')} ${displayTimeZone}`;
  }, [reservedAt, displayTimeZone]);

  return (
    <FloatingModal isOpen={isOpen} onRequestClose={onRequestClose}>
      <div className={styles.releaseReminderCompletedModal}>
        <h2>{t('releaseReminder.completed.heading')}</h2>

        {creation && (
          <div className={styles.reservedItemDetails}>
            <SquareCreationThumbnail creation={creation} size={140} />

            <div>
              <div className={styles.releaseDate}>
                <h3>{t('releaseReminder.completed.releaseDate')}</h3>
                <div>{reservationDate}</div>
                <div>{reservationTime}</div>
              </div>

              <div className={styles.creator}>
                <h3>{t('releaseReminder.completed.creator')}</h3>

                <Link href={routes.creatorDetail(creation.creator.userName)}>
                  <a>
                    <FramedIcon
                      imageURL={creation.creator.profileImageURL}
                      certified
                    />
                    {(i18n.language === 'ja' &&
                      creation.creator.displayNameJA) ||
                      creation.creator.displayName}
                  </a>
                </Link>
              </div>
            </div>
          </div>
        )}

        <CustomButton
          variant="primary"
          onClick={onRequestClose}
          className={styles.confirmButton}
        >
          {t('releaseReminder.completed.confirmButton')}
        </CustomButton>
      </div>
    </FloatingModal>
  );
};

export default ReleaseReminderCompletedModal;
